import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { istIsoTag, pruefeFormular } from '../../../services/sifa/teilnahmeLogic';
import { formatiereDatum, tippDatumMaske, tippDatumWert, type TeilnahmeFormular } from './teilnahmeSeiteLogic';
import { EINGABE, EINGABE_FEHLER, FEHLERKASTEN, KNOPF_PRIMAER, KNOPF_SEKUNDAER } from './teilnahmeSeiteStil';

const VERSTANDEN_TEXT = 'Ich habe die Unterweisung verstanden und werde die Inhalte beachten.';

/**
 * Angaben zur Person. Fehlertexte kommen aus pruefeFormular (teilnahmeLogic,
 * Alter ≥ 14 usw.) und erscheinen erst nach dem ersten Absenden — sonst
 * meckert die Seite, bevor der Teilnehmer ueberhaupt zu tippen anfaengt.
 *
 * Geburtsdatum: Kalender oder Eintippen. Am Handy oeffnet type=date nur das
 * Rad bzw. den Kalender, und bis 1975 zurueckzuwischen dauert — „Eintippen"
 * gibt ein Ziffernfeld TT.MM.JJJJ, die Punkte setzt tippDatumMaske.
 */
export const FormularAnsicht: React.FC<{
  formular: TeilnahmeFormular;
  verstanden: boolean;
  onAendern: (feld: keyof TeilnahmeFormular, wert: string) => void;
  onVerstanden: (wert: boolean) => void;
  onWeiter: () => void;
  onZurueck: () => void;
}> = ({ formular, verstanden, onAendern, onVerstanden, onWeiter, onZurueck }) => {
  const [abgeschickt, setAbgeschickt] = useState(false);
  // Halb getippter Text aus dem sessionStorage (Neuladen) bleibt im Tippfeld.
  const [datumTippen, setDatumTippen] = useState(() => !!formular.geburtsdatum && !istIsoTag(formular.geburtsdatum));
  // Das Tippfeld zeigt, was getippt wurde („1.3.1985"), nicht das gespeicherte
  // ISO zurueckformatiert („01.03.1985") — jede Umschreibung versetzt die Schreibmarke.
  const [datumText, setDatumText] = useState(() => formatiereDatum(formular.geburtsdatum));

  const datumModus = (tippen: boolean) => {
    if (tippen === datumTippen) return;
    // Der Kalender kann halben Text nicht zeigen — leer statt „ungültig" neben einem leeren Feld.
    if (!tippen && formular.geburtsdatum && !istIsoTag(formular.geburtsdatum)) onAendern('geburtsdatum', '');
    // Fokus noch im Tipp-Ereignis, sonst oeffnet iOS die Tastatur nicht.
    flushSync(() => {
      setDatumTippen(tippen);
      if (tippen) setDatumText(formatiereDatum(formular.geburtsdatum));
    });
    if (tippen) document.getElementById('formular-geburtsdatum')?.focus();
  };

  const fehler = abgeschickt ? pruefeFormular(formular) : [];
  const fehlerFuer = (feld: keyof TeilnahmeFormular) => fehler.find((f) => f.feld === feld)?.text;
  const verstandenFehlt = abgeschickt && !verstanden;

  const absenden = (e: React.FormEvent) => {
    e.preventDefault();
    setAbgeschickt(true);
    if (pruefeFormular(formular).length === 0 && verstanden) onWeiter();
  };

  const feld = (
    name: keyof TeilnahmeFormular,
    label: string,
    typ: string,
    autoComplete: string,
    extra?: React.InputHTMLAttributes<HTMLInputElement>,
    zusatz?: React.ReactNode,
  ) => {
    const text = fehlerFuer(name);
    const id = `formular-${name}`;
    return (
      <div className="space-y-1.5">
        <div className="flex items-end justify-between gap-3">
          <label htmlFor={id} className="block text-base font-semibold text-slate-700">
            {label}
          </label>
          {zusatz}
        </div>
        <input
          id={id}
          name={name}
          type={typ}
          autoComplete={autoComplete}
          value={formular[name]}
          onChange={(e) => onAendern(name, e.target.value)}
          aria-invalid={text ? true : undefined}
          aria-describedby={text ? `${id}-fehler` : undefined}
          className={`${EINGABE} ${text ? EINGABE_FEHLER : ''}`}
          {...extra}
        />
        {text && (
          <p id={`${id}-fehler`} className="text-sm text-red-600">
            {text}
          </p>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={absenden} noValidate className="space-y-5">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-slate-900">Deine Angaben</h2>
        <p className="text-slate-600">Diese Angaben stehen auf deinem Nachweis. Bitte so, wie sie im Ausweis stehen.</p>
      </div>

      {feld('vorname', 'Vorname', 'text', 'given-name')}
      {feld('nachname', 'Nachname', 'text', 'family-name')}
      {feld(
        'geburtsdatum',
        'Geburtsdatum',
        datumTippen ? 'text' : 'date',
        'bday',
        datumTippen
          ? {
              inputMode: 'numeric',
              placeholder: 'TT.MM.JJJJ',
              value: datumText,
              onChange: (e) => {
                setDatumText(tippDatumMaske(e.target.value));
                onAendern('geburtsdatum', tippDatumWert(e.target.value));
              },
            }
          : { max: new Date().toISOString().slice(0, 10) },
        <div role="group" aria-label="Geburtsdatum eingeben" className="inline-flex shrink-0 p-0.5 rounded-lg bg-slate-100 border border-slate-200">
          {[
            { tippen: false, text: 'Kalender' },
            { tippen: true, text: 'Eintippen' },
          ].map((m) => (
            <button
              key={m.text}
              type="button"
              aria-pressed={datumTippen === m.tippen}
              onClick={() => datumModus(m.tippen)}
              className={`px-3 min-h-[40px] rounded-md text-sm font-semibold transition-colors ${
                datumTippen === m.tippen ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {m.text}
            </button>
          ))}
        </div>,
      )}
      {feld('email', 'E-Mail-Adresse', 'email', 'email', { inputMode: 'email', placeholder: 'name@beispiel.de' })}

      <div className="space-y-1.5">
        <label
          htmlFor="formular-verstanden"
          className={`flex items-start gap-3 p-4 min-h-[56px] rounded-xl border-2 cursor-pointer transition-all ${
            verstanden ? 'border-blue-600 bg-blue-50' : verstandenFehlt ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white'
          }`}
        >
          <input
            id="formular-verstanden"
            name="verstanden"
            type="checkbox"
            checked={verstanden}
            onChange={(e) => onVerstanden(e.target.checked)}
            aria-invalid={verstandenFehlt ? true : undefined}
            aria-describedby={verstandenFehlt ? 'formular-verstanden-fehler' : undefined}
            className="mt-0.5 w-6 h-6 shrink-0 accent-blue-600"
          />
          <span className="text-base text-slate-800 leading-snug">{VERSTANDEN_TEXT}</span>
        </label>
        {verstandenFehlt && (
          <p id="formular-verstanden-fehler" className="text-sm text-red-600">
            Bitte bestätige, dass du die Unterweisung verstanden hast.
          </p>
        )}
      </div>

      {abgeschickt && (fehler.length > 0 || verstandenFehlt) && (
        <div className={FEHLERKASTEN} role="alert">
          Bitte prüfe die markierten Felder.
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
        <button type="button" onClick={onZurueck} className={KNOPF_SEKUNDAER}>
          Zurück
        </button>
        <button type="submit" className={KNOPF_PRIMAER}>
          Weiter zur Unterschrift
        </button>
      </div>
    </form>
  );
};
